import {
  useMemo,
  useRef,
  useState,
  useEffect,
  type CSSProperties,
} from "react";

import { Chess, type Move, type Square } from "chess.js";
import { createPortal } from "react-dom";

import { lichessBoardPalette } from "@/shared/config/siteContent";
import { useI18n } from "@/shared/i18n/LanguageContext";
import { replayUciMove, resolveUciMove } from "@/shared/ui/chessMove";
import { ChessSurface } from "@/shared/ui/ChessSurface";

import { previewOpeningSeed } from "./openingSeedApi";
import {
  selectionFromPreview,
  type OpeningSeedSelection,
  type OpeningSeedSource,
} from "./openingSeedTypes";

type BoardOrientation = "white" | "black";
type OpeningSeedDialogProps = {
  initialSeed: OpeningSeedSelection | null;
  onConfirm: (seed: OpeningSeedSelection | null) => void;
  onClose: () => void;
};

function moveToUci(move: Move) {
  return `${move.from}${move.to}${move.promotion ?? ""}`;
}

function buildGameFromMoves(moves: string[], count = moves.length) {
  const game = new Chess();
  for (const move of moves.slice(0, count)) {
    replayUciMove(game, move);
  }
  return game;
}

function buildSelectionFromMoves(
  source: OpeningSeedSource,
  moves: string[],
  label?: string | null,
): OpeningSeedSelection {
  const game = new Chess();
  const sanMoves: string[] = [];
  for (const uci of moves) {
    const move = replayUciMove(game, uci);
    if (!move) {
      throw new Error(`illegal move: ${uci}`);
    }
    sanMoves.push(move.san);
  }
  return {
    source,
    moves,
    sanMoves,
    selectedPly: moves.length,
    finalFen: game.fen(),
    sideToMove: game.turn() === "w" ? "white" : "black",
    label,
    headers: {},
    warnings: [],
  };
}

function buildMovePairs(sanMoves: string[]) {
  const pairs: Array<{
    moveNumber: number;
    white?: string;
    black?: string;
    whitePly: number;
    blackPly: number;
  }> = [];
  for (let index = 0; index < sanMoves.length; index += 2) {
    pairs.push({
      moveNumber: Math.floor(index / 2) + 1,
      white: sanMoves[index],
      black: sanMoves[index + 1],
      whitePly: index + 1,
      blackPly: index + 2,
    });
  }
  return pairs;
}

function seedSummary(
  seed: OpeningSeedSelection | null,
  labels: { standardStart: string; white: string; black: string; toMove: string },
) {
  if (!seed || seed.moves.length === 0) {
    return labels.standardStart;
  }
  const side = seed.sideToMove === "white" ? labels.white : labels.black;
  return `${seed.moves.length} ply - ${labels.toMove.replace("{side}", side)}`;
}

export function OpeningSeedDialog({
  initialSeed,
  onConfirm,
  onClose,
}: OpeningSeedDialogProps) {
  const { copy } = useI18n();
  const [tab, setTab] = useState<OpeningSeedSource>(initialSeed?.source ?? "manual");
  const [manualMoves, setManualMoves] = useState<string[]>(initialSeed?.moves ?? []);
  const [pgnText, setPgnText] = useState("");
  const [pgnPreview, setPgnPreview] = useState<OpeningSeedSelection | null>(
    initialSeed?.source === "pgn" ? initialSeed : null,
  );
  const [pgnSelectedPly, setPgnSelectedPly] = useState(
    initialSeed?.source === "pgn" ? initialSeed.selectedPly : 0,
  );
  const [selectedSquare, setSelectedSquare] = useState<Square | null>(null);
  const [legalTargets, setLegalTargets] = useState<Square[]>([]);
  const [orientation, setOrientation] = useState<BoardOrientation>("white");
  const [boardWidth, setBoardWidth] = useState(0);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boardWrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = boardWrapRef.current;
    if (!node) {
      return;
    }
    const updateWidth = () => {
      const chromeWidth = window.matchMedia("(max-width: 560px)").matches ? 42 : 52;
      const next = Math.floor(Math.min(Math.max(node.clientWidth - chromeWidth, 220), 440));
      setBoardWidth(next > 0 ? next : 420);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const activeMoves = tab === "pgn"
    ? pgnPreview?.moves.slice(0, pgnSelectedPly) ?? []
    : manualMoves;
  const activeSeed = useMemo(() => {
    try {
      return buildSelectionFromMoves(tab, activeMoves, tab === "pgn" ? "PGN line" : null);
    } catch {
      return null;
    }
  }, [activeMoves, tab]);
  const boardGame = useMemo(() => buildGameFromMoves(activeMoves), [activeMoves]);
  const lastMove = useMemo(
    () => (boardGame.history({ verbose: true }) as Move[]).at(-1) ?? null,
    [boardGame],
  );
  const manualSeed = useMemo(() => {
    try {
      return buildSelectionFromMoves("manual", manualMoves);
    } catch {
      return null;
    }
  }, [manualMoves]);
  const pgnPairs = useMemo(
    () => buildMovePairs(pgnPreview?.sanMoves ?? []),
    [pgnPreview],
  );
  const manualPairs = useMemo(
    () => buildMovePairs(manualSeed?.sanMoves ?? []),
    [manualSeed],
  );

  const selectSquare = (square: Square) => {
    const moves = boardGame.moves({ square, verbose: true });
    if (moves.length === 0) {
      setSelectedSquare(null);
      setLegalTargets([]);
      return;
    }
    setSelectedSquare(square);
    setLegalTargets(moves.map((move) => move.to));
  };

  const commitManualMove = (from: Square, to: Square) => {
    const next = buildGameFromMoves(manualMoves);
    const uci = resolveUciMove(next, from, to);
    if (!uci) {
      return false;
    }
    replayUciMove(next, uci);
    setManualMoves((current) => [...current, uci]);
    setSelectedSquare(null);
    setLegalTargets([]);
    setError(null);
    return true;
  };

  const onSquareClick = (square: Square) => {
    if (tab !== "manual") {
      return;
    }
    if (selectedSquare) {
      if (square === selectedSquare) {
        setSelectedSquare(null);
        setLegalTargets([]);
        return;
      }
      if (commitManualMove(selectedSquare, square)) {
        return;
      }
    }

    const piece = boardGame.get(square);
    if (!piece || piece.color !== boardGame.turn()) {
      setSelectedSquare(null);
      setLegalTargets([]);
      return;
    }
    selectSquare(square);
  };

  const onPieceDrop = (sourceSquare: Square, targetSquare?: Square) => {
    if (tab !== "manual" || !targetSquare) {
      return false;
    }
    return commitManualMove(sourceSquare, targetSquare);
  };

  const parsePgn = async () => {
    setError(null);
    setIsParsing(true);
    try {
      const preview = await previewOpeningSeed({
        source: "pgn",
        pgn: pgnText,
      });
      const next = selectionFromPreview(preview);
      setPgnPreview(next);
      setPgnSelectedPly(next.selectedPly);
    } catch (parseError) {
      setPgnPreview(null);
      setPgnSelectedPly(0);
      setError(parseError instanceof Error ? parseError.message : "Failed to parse PGN");
    } finally {
      setIsParsing(false);
    }
  };

  const confirm = async () => {
    setError(null);
    const seed = tab === "pgn"
      ? pgnPreview
        ? {
            ...buildSelectionFromMoves("pgn", pgnPreview.moves.slice(0, pgnSelectedPly), "PGN line"),
            headers: pgnPreview.headers,
          }
        : null
      : manualSeed;
    if (!seed || seed.moves.length === 0) {
      onConfirm(null);
      return;
    }
    try {
      const checked = await previewOpeningSeed({
        source: seed.source,
        moves: seed.moves,
        selectedPly: seed.moves.length,
        label: seed.label,
      });
      onConfirm(selectionFromPreview(checked));
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : "Invalid start position");
    }
  };

  const customSquareStyles = useMemo(() => {
    const styles: Record<string, CSSProperties> = {};
    if (selectedSquare && tab === "manual") {
      styles[selectedSquare] = {
        boxShadow: lichessBoardPalette.selected,
      };
    }
    if (tab === "manual") {
      for (const square of legalTargets) {
        styles[square] = {
          background: lichessBoardPalette.legalTarget,
        };
      }
    }
    if (lastMove) {
      styles[lastMove.from] = {
        ...(styles[lastMove.from] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
      styles[lastMove.to] = {
        ...(styles[lastMove.to] ?? {}),
        backgroundColor: lichessBoardPalette.lastMove,
      };
    }
    return styles;
  }, [lastMove, legalTargets, selectedSquare, tab]);

  const movePairs = tab === "pgn" ? pgnPairs : manualPairs;
  const canUse = activeMoves.length > 0;

  return createPortal(
    <div className="opening-seed-backdrop" role="presentation" onClick={onClose}>
      <section
        className="opening-seed-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="opening-seed-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button className="opening-seed-dialog__close" type="button" onClick={onClose}>
          {copy.play.opening.dismiss}
        </button>
        <header className="opening-seed-dialog__head">
          <p className="challenge-modal__label">{copy.play.opening.eyebrow}</p>
          <h3 id="opening-seed-title">{copy.play.opening.title}</h3>
          <span>
            {seedSummary(activeSeed, {
              white: copy.play.modal.white,
              black: copy.play.modal.black,
              standardStart: copy.play.opening.standardStart,
              toMove: copy.play.opening.toMove,
            })}
          </span>
        </header>

        <div className="opening-seed-tabs" role="tablist" aria-label="Opening seed source">
          <button
            className={tab === "manual" ? "opening-seed-tab opening-seed-tab--active" : "opening-seed-tab"}
            type="button"
            onClick={() => {
              setTab("manual");
              setSelectedSquare(null);
              setLegalTargets([]);
              setError(null);
            }}
          >
            {copy.play.opening.boardTab}
          </button>
          <button
            className={tab === "pgn" ? "opening-seed-tab opening-seed-tab--active" : "opening-seed-tab"}
            type="button"
            onClick={() => {
              setTab("pgn");
              setSelectedSquare(null);
              setLegalTargets([]);
              setError(null);
            }}
          >
            {copy.play.opening.pgnTab}
          </button>
        </div>

        <div className="opening-seed-layout">
          <div className="opening-seed-board-panel" ref={boardWrapRef}>
            {boardWidth ? (
              <ChessSurface
                id="opening-seed-board"
                boardWidth={boardWidth}
                position={boardGame.fen()}
                orientation={orientation}
                arePiecesDraggable={tab === "manual"}
                onPieceDrop={onPieceDrop}
                onPieceDragBegin={(_piece, square) => tab === "manual" && selectSquare(square)}
                onSquareClick={onSquareClick}
                customSquareStyles={customSquareStyles}
                lightSquareColor="#eed6ad"
                darkSquareColor="#b88963"
              />
            ) : null}
            <div className="opening-seed-board-actions">
              <button type="button" onClick={() => setOrientation((current) => current === "white" ? "black" : "white")}>
                {copy.play.opening.flip}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (tab === "manual") {
                    setManualMoves((current) => current.slice(0, -1));
                  } else {
                    setPgnSelectedPly((current) => Math.max(0, current - 1));
                  }
                  setSelectedSquare(null);
                  setLegalTargets([]);
                }}
                disabled={activeMoves.length === 0}
              >
                {copy.play.opening.undo}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (tab === "manual") {
                    setManualMoves([]);
                  } else {
                    setPgnSelectedPly(0);
                  }
                  setSelectedSquare(null);
                  setLegalTargets([]);
                }}
                disabled={activeMoves.length === 0}
              >
                {copy.play.opening.reset}
              </button>
            </div>
          </div>

          <aside className="opening-seed-side">
            {tab === "pgn" ? (
              <div className="opening-seed-pgn">
                <textarea
                  value={pgnText}
                  onChange={(event) => setPgnText(event.target.value)}
                  placeholder={copy.play.opening.pgnPlaceholder}
                />
                <button className="button-link button-link--secondary" type="button" onClick={parsePgn} disabled={isParsing}>
                  {isParsing ? copy.play.opening.parsing : copy.play.opening.loadPgn}
                </button>
              </div>
            ) : null}

            <div className="opening-seed-moves">
              <div className="opening-seed-moves__head">
                <span>{copy.play.opening.moveLine}</span>
                <strong>{activeMoves.length} ply</strong>
              </div>
              {movePairs.length ? (
                <ol className="opening-seed-move-list">
                  {movePairs.map((pair) => (
                    <li key={pair.moveNumber}>
                      <span>{pair.moveNumber}.</span>
                      {pair.white ? (
                        <button
                          type="button"
                          onClick={() => tab === "pgn" && setPgnSelectedPly(pair.whitePly)}
                          data-active={tab === "pgn" && pgnSelectedPly === pair.whitePly}
                          disabled={tab !== "pgn"}
                        >
                          {pair.white}
                        </button>
                      ) : null}
                      {pair.black ? (
                        <button
                          type="button"
                          onClick={() => tab === "pgn" && setPgnSelectedPly(pair.blackPly)}
                          data-active={tab === "pgn" && pgnSelectedPly === pair.blackPly}
                          disabled={tab !== "pgn"}
                        >
                          {pair.black}
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="opening-seed-empty">{copy.play.opening.empty}</p>
              )}
            </div>

            {error ? <p className="challenge-modal__error">{error}</p> : null}
          </aside>
        </div>

        <footer className="opening-seed-footer">
          <button className="button-link button-link--secondary" type="button" onClick={() => onConfirm(null)}>
            {copy.play.opening.clear}
          </button>
          <button className="button-link button-link--secondary" type="button" onClick={onClose}>
            {copy.play.modal.cancel}
          </button>
          <button className="button-link button-link--primary" type="button" onClick={confirm} disabled={!canUse}>
            {copy.play.opening.usePosition}
          </button>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
